import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import Layout from '../../layout/Layout';
import { Link, useNavigate, useParams } from 'react-router-dom';
import poManagementNotesIcon from '../../../assets/invoicesNotesIcon.png';
import { useDispatch, useSelector } from 'react-redux';
import { createPurchaseOrder, updatePurchaseOrder } from '../../../store/slices/purchaseOrderSlice';
import { PurchaseOrderItem, CreatePurchaseOrderRequest, PurchaseOrderStatus } from '../../../store/types';
import { getInvoices, InvoiceResponse } from '../../../services/invoiceService';
import supplierService, { Supplier } from '../../../services/supplierService';
import { v4 as uuidv4 } from 'uuid';
import inventoryService, { Inventory } from '../../../services/inventoryService';
import { BackLinkComponent } from '../../ui/DesignSystem';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { useToast } from '../../../hooks/useToast';
import { Toast as ToastComponent } from '../../common/Toast';
import { useStoreFilter } from '../../../hooks/useStoreFilter';
import ExitConfirmationDialog from '../../common/ExitConfirmationDialog';
import { useUnsavedChanges } from '../../../context/UnsavedChangesContext';
import SkuImage, { markSkusAsRecentlyUploaded } from '../../common/SkuImage';
import { API_URL, ENDPOINTS } from '../../../config';
import { getAuthToken, isAuthenticated, ensureDevelopmentAuth } from '../../../utils/auth';
import purchaseOrderService from '../../../services/purchaseOrderService';
import { AppDispatch, RootState } from '../../../store';
import invoiceService from '../../../services/invoiceService';
import debounce from 'lodash/debounce';

// Add CSS animation for loading spinner
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Global style to force Notes column width and last column width
const GlobalTableStyle = createGlobalStyle`
  table[class*="Table"] colgroup col:first-child,
  table colgroup col:first-child {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
  }
  
  table[class*="Table"] thead tr th:first-child,
  table thead tr th:first-child,
  table[class*="Table"] tbody tr td:first-child,
  table tbody tr td:first-child {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
  
  table[class*="Table"] colgroup col:last-child,
  table colgroup col:last-child {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
  }
  
  table[class*="Table"] thead tr th:last-child,
  table thead tr th:last-child,
  table[class*="Table"] tbody tr td:last-child,
  table tbody tr td:last-child {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
  }
  
  /* Force the Total (ex GST) column - 6th column */
  table[class*="Table"] colgroup col:nth-child(6),
  table colgroup col:nth-child(6) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }
  
  table[class*="Table"] thead tr th:nth-child(6),
  table thead tr th:nth-child(6),
  table[class*="Table"] tbody tr td:nth-child(6),
  table tbody tr td:nth-child(6) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
`;

const PageHeader = styled.div`
  padding: 1.5rem 2rem;
`;

// New back button styling based on EditInvoiceForm
const StyledBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #042B41;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(4, 43, 65, 0.05);
  }
  
  svg {
    stroke-width: 2px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
`;

// New styling for breadcrumb navigation
const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
`;

const BreadcrumbItem = styled.span`
  font-size: 1.1rem;
  color: #6B7280;
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 0.5rem;
  color: #6B7280;
`;

const BreadcrumbLink = styled(Link)`
  color: #042B41;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 500;
  
  &:hover {
    color: #0A3D5A;
    text-decoration: underline;
  }
`;

const PageTitle = styled.span`
  font-weight: 700;
  color: #111827;
  font-size: 1.25rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  font-size: 14px;
  color: #374151;
  margin-bottom: 0.5rem;
  font-weight: 500;
  
  .required {
    color: #EF4444;
    margin-left: 4px;
  }
`;

const FormInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  
  &:focus {
    outline: none;
    border-color: #6B7280;
    box-shadow: 0 0 0 1px #6B7280;
  }
  
  &:disabled {
    background-color: #F9FAFB;
    cursor: not-allowed;
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  background-color: white;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #6B7280;
    box-shadow: 0 0 0 1px #6B7280;
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  
  input[type="date"] {
    width: 100%;
    padding-right: 40px; /* Make space for the calendar icon */
    cursor: pointer;
    
    /* Hide the default calendar icon on webkit browsers */
    &::-webkit-calendar-picker-indicator {
      opacity: 0;
      position: absolute;
      right: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
    
    /* Ensure the entire input area is clickable */
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
  
  .calendar-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 2rem;
  overflow: visible;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  table-layout: fixed;
  overflow: visible;
  
  th, td {
    box-sizing: border-box;
  }
  
  /* Force the first column in colgroup to be exactly 50px */
  colgroup col:first-child {
    width: 100px !important;
    min-width: 100px !important;
    max-width: 100px !important;
  }
  
  /* Force the first column cells to be exactly 50px - highest specificity */
  thead tr th:first-child,
  tbody tr td:first-child {
    width: 50px !important;
    min-width: 50px !important;
    max-width: 50px !important;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
  
  /* Force the last column in colgroup to be exactly 80px */
  colgroup col:last-child {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
  }
  
  /* Force the last column cells to be exactly 80px */
  thead tr th:last-child,
  tbody tr td:last-child {
    width: 80px !important;
    min-width: 80px !important;
    max-width: 80px !important;
  }
  
  /* Style the Quantity Ordered column - 4th column */
  colgroup col:nth-child(4) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }
  
  thead tr th:nth-child(4),
  tbody tr td:nth-child(4) {
    width: 125px !important;
    min-width: 125px !important;
    max-width: 125px !important;
  }
  
  /* Style the Expected Delivery Date column - 5th column */
  colgroup col:nth-child(5) {
    width: 250px !important;
    min-width: 300px !important;
    max-width: 300px !important;
  }
  
  thead tr th:nth-child(5),
  tbody tr td:nth-child(5) {
    width: 190px !important;
    min-width: 190px !important;
    max-width: 190px !important;
  }
  
  /* Style the Total (ex GST) column - 6th column */
  colgroup col:nth-child(6) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
  }
  
  thead tr th:nth-child(6),
  tbody tr td:nth-child(6) {
    width: 250px !important;
    min-width: 250px !important;
    max-width: 250px !important;
    text-align: right;
    padding-right: 12px;
  }
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  
  th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-weight: 500;
    font-size: 14px;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    &.notes-column-header,
    &:first-child {
      padding: 0.75rem 4px !important;
      text-align: center !important;
      width: 90px !important;
      min-width: 90px !important;
      max-width: 90px !important;
      box-sizing: border-box !important;
    }
    
    &:last-child {
      border-right: none;
      width: 90px !important;
      min-width: 90px !important;
      max-width: 90px !important;
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    &:nth-child(even) {
      background-color: #F9FAFB;
    }
    
    &:hover {
      background-color: #F3F4F6;
    }
  }
  
  td {
    padding: 0.75rem 1rem;
    font-size: 14px;
    color: #111827;
    border: 1px solid #E5E7EB;
    vertical-align: middle;
    position: relative;
    
    &:last-child {
      text-align: center;
    }
    
    &.notes-column-cell,
    &:first-child {
      overflow: visible;
      padding: 8px 4px !important;
      text-align: center !important;
      width: 50px !important;
      min-width: 50px !important;
      max-width: 50px !important;
      box-sizing: border-box !important;
    }
    
    &:last-child {
      width: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
    }
    
    &.wrap {
      white-space: normal;
      word-break: break-word;
      overflow: visible;
    }
  }
  
  input, select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #E5E7EB;
    border-radius: 4px;
    font-size: 14px;
    background-color: transparent;
  }
  
  .numeric-input {
    text-align: right;
  }
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const AddButton = styled.button`
  background-color: #042B41;
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  
  &:hover {
    background-color: #031F30;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const RemoveButton = styled.button`
  background-color: white;
  color: #EF4444;
  border: 1px solid #EF4444;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  
  &:hover {
    background-color: #FEF2F2;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  padding: 0;
`;

const NotesIcon = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 36px;
  margin: 0 auto;
  
  img {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 2rem;
`;

const PaymentTermsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 400px;
  margin-bottom: 1.5rem;
  
  label {
    margin-bottom: 0;
    white-space: nowrap;
  }
  
  input {
    flex: 1;
  }
`;

const CostsContainer = styled.div`
  flex: 1;
  max-width: 300px;
`;

const CostItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-size: 14px;
  color: #374151;
  width: 400px;
  
  &:last-child {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #E5E7EB;
    font-weight: 600;
  }
  
  .amount {
    font-weight: 500;
    background-color: #f9fafb;
    padding: 0.5rem;
    min-width: 120px;
    text-align: right;
    border-radius: 4px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
`;

const CancelButton = styled(Button)`
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #042B41;
  color: white;
  border: none;
  
  &:hover {
    background-color: #031f30;
  }
`;

const DraftButton = styled(Button)`
  background-color: white;
  color: #042B41;
  border: 1px solid #042B41;
  
  &:hover {
    background-color: #f0f5f9;
  }
`;

// Modal components for Notes
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 20px 0;
  text-align: center;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 20px;
  resize: none;
  
  &:focus {
    outline: none;
    border-color: #042B41;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  flex: 1;
`;

const CloseButton = styled(ModalButton)`
  background-color: white;
  color: #333;
  border: 1px solid #e0e0e0;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const SaveButton = styled(ModalButton)`
  background-color: #042B41;
  color: white;
  border: none;
  
  &:hover {
    background-color: #031f30;
  }
`;

const ReadOnlyInput = styled.div`
  padding: 0.75rem 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 14px;
  color: #6B7280;
  background-color: #F9FAFB;
  cursor: not-allowed;
  display: flex;
  align-items: center;
`;

const StyledFormSelect = styled(FormSelect)`
  appearance: none;
  padding-right: 30px;
  background-position: right 15px center;
  background-size: 12px;
  cursor: pointer;
  transition: border-color 0.2s;
  
  &:hover {
    border-color: #9CA3AF;
  }
  
  &:focus {
    border-color: #2563EB;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

const CurrencyInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f9fafb;
  border-radius: 4px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  span {
    position: absolute;
    left: 0.5rem;
    color: #6B7280;
    z-index: 1;
  }
  
  input {
    padding-left: 1.5rem;
    padding-right: 0.5rem;
    text-align: right;
    background-color: transparent;
    border: none;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
    }
  }
`;

// SKU Select component - searchable with images
const SKUSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SKUSearchInput = styled(FormInput)`
  width: 100%;
`;

const SKUDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #D1D5DB;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  width: 100%;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const SKUOption = styled.div`
  padding: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  background-color: white;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: #F3F4F6;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SKUOptionContent = styled.div`
  flex: 1;
`;

const SelectedSKUContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

// Add refresh button styles
const RefreshButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px 4px;
  margin-left: 8px;
  border-radius: 3px;
  font-size: 12px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HeaderWithRefresh = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

// Simplified interfaces for dropdown data
interface SimpleInvoice {
  id: string | number;
  display_text: string;
  invoice_number?: any;
  formatted_number?: string;
}

interface SimpleSupplier {
  id: string | number;
  display_text: string;
  supplier_name?: string;
  name?: string;
}

// Add BWA BUY PRICE interface and state
interface BWAPricing {
  [sku: string]: number; // Map SKU to BWA BUY PRICE
}

interface POItem {
  id: string;
  sku: string;
  description: string;
  quantityOrdered: string | number;
  expectedDeliveryDate: string;
  total: number;
  notes: string;
  bwaBuyPrice?: number; // Add BWA BUY PRICE field
  supplierId?: string; // Add supplier ID for filtering by supplier when loading from invoice
}

// Add skeleton loading components
const SkeletonLoader = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  height: 42px;
  width: 100%;
  
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ButtonSkeleton = styled(SkeletonLoader)`
  height: 44px;
  width: 100%;
  display: inline-block;
`;

// Safety wrapper to prevent object-as-child errors
const SafeText: React.FC<{ children: any }> = ({ children }) => {
  if (children == null) return null;
  if (typeof children === 'string' || typeof children === 'number') {
    return <>{children}</>;
  }
  if (typeof children === 'object') {
    console.warn('⚠️ SafeText: Converting object to string:', children);
    return <>{String(children)}</>;
  }
  return <>{children}</>;
};

interface CreatePOPageProps {
  isEditMode?: boolean;
}

const CreatePOPage: React.FC<CreatePOPageProps> = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id: poId } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [loadingPO, setLoadingPO] = useState(isEditMode);
  const { loading: purchaseOrdersLoading, error } = useSelector((state: RootState) => state.purchaseOrders);
  const toast = useToast();
  const storeFilterHook = useStoreFilter();
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Memoize storeFilter to prevent infinite re-renders
  const storeFilter = useMemo(() => ({
    shouldFilterByStore: storeFilterHook.shouldFilterByStore,
    userStoreType: storeFilterHook.userStoreType,
    userStoreTypeId: storeFilterHook.userStoreTypeId,
    isStoreRestricted: storeFilterHook.isStoreRestricted
  }), [
    storeFilterHook.shouldFilterByStore,
    storeFilterHook.userStoreType,
    storeFilterHook.userStoreTypeId,
    storeFilterHook.isStoreRestricted
  ]);

  const [storeType, setStoreType] = useState('');
  const [invoiceId, setInvoiceId] = useState<string>('');
  
  // Simplified invoice state
  const [invoices, setInvoices] = useState<SimpleInvoice[]>([]);
  
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [supplier, setSupplier] = useState('');
  const [supplierId, setSupplierId] = useState<string>('');
  
  // Simplified supplier state
  const [suppliers, setSuppliers] = useState<SimpleSupplier[]>([]);
  
  const [paymentTerms, setPaymentTerms] = useState('Net 30');

  // Add refs to track component mount state and prevent duplicate calls
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initializationRef = useRef(false);
  const skipNextInvoiceFetchRef = useRef(false);

  // Add flag to prevent auto-calculation when loading from invoice
  const [isLoadingFromInvoice, setIsLoadingFromInvoice] = useState(false);
  
  // Add loading state for invoice details
  const [isLoadingInvoiceDetails, setIsLoadingInvoiceDetails] = useState(false);

  const [allInvoiceItems, setAllInvoiceItems] = useState<POItem[]>([]);
  const [items, setItems] = useState<POItem[]>([
    {
      id: uuidv4(),
      sku: '',
      description: '',
      quantityOrdered: '',
      expectedDeliveryDate: '',
      total: 0,
      notes: ''
    }
  ]);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNotes, setCurrentNotes] = useState('');
  const [currentItemId, setCurrentItemId] = useState('');

  const [totalAmount, setTotalAmount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);

  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<Inventory[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState<boolean>(false);
  const [lastInventoryRefresh, setLastInventoryRefresh] = useState<number>(Date.now());

  // SKU dropdown state (still needed for SKU search functionality)
  const [skuSearchTerms, setSKUSearchTerms] = useState<{ [key: string]: string }>({});
  const [openSKUDropdowns, setOpenSKUDropdowns] = useState<{ [key: string]: boolean }>({});

  // Add BWA pricing state
  const [bwaPricing, setBwaPricing] = useState<BWAPricing>({});
  const [isLoadingBWAPricing, setIsLoadingBWAPricing] = useState<{ [key: string]: boolean }>({});

  // Add state for filtered inventory items by row
  const [filteredInventoryItemsByRow, setFilteredInventoryItemsByRow] = useState<{ [key: string]: any[] }>({});

  // Add state for exit confirmation
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [initialFormState, setInitialFormState] = useState<{
    storeType: string;
    invoiceId: string;
    issueDate: string;
    supplier: string;
    supplierId: string;
    paymentTerms: string;
    items: POItem[];
  } | null>(null);

  // Initialize baseline data
  useEffect(() => {
    if (!initialFormState) {
      setInitialFormState({
        storeType,
        invoiceId,
        issueDate,
        supplier,
        supplierId,
        paymentTerms,
        items: JSON.parse(JSON.stringify(items))
      });
    }
  }, []);

  // Function to check if form has changed
  const hasFormDataChanged = (): boolean => {
    if (!initialFormState) return false;

    if (
      storeType !== initialFormState.storeType ||
      invoiceId !== initialFormState.invoiceId ||
      issueDate !== initialFormState.issueDate ||
      supplier !== initialFormState.supplier ||
      supplierId !== initialFormState.supplierId ||
      paymentTerms !== initialFormState.paymentTerms
    ) {
      return true;
    }

    // Compare items
    if (items.length !== initialFormState.items.length) return true;

    for (let i = 0; i < items.length; i++) {
      const current = items[i];
      const initial = initialFormState.items[i];
      if (
        current.sku !== initial.sku ||
        current.description !== initial.description ||
        current.quantityOrdered !== initial.quantityOrdered ||
        current.expectedDeliveryDate !== initial.expectedDeliveryDate ||
        current.total !== initial.total ||
        current.notes !== initial.notes ||
        current.bwaBuyPrice !== initial.bwaBuyPrice
      ) {
        return true;
      }
    }

    return false;
  };

  // Use debounce to prevent excessive API calls
  const debouncedSearchSKUs = useCallback(
    debounce(async (searchTerm: string, itemId: string) => {
      if (!searchTerm.trim() || !supplier || !supplierId) {
        setFilteredInventoryItemsByRow(prev => ({
          ...prev,
          [itemId]: []
        }));
        return;
      }

      setIsLoadingInventory(true);
      try {
        // Use backend search through inventoryService
        const results = await inventoryService.searchSKUs(searchTerm, 10);
        
        // Filter results by supplier
        const supplierLower = supplier.toLowerCase();
        const filteredResults = results.filter(item => {
          const itemSupplierName = item.supplier_name?.toLowerCase() || '';
          return itemSupplierName === supplierLower || 
                 itemSupplierName.includes(supplierLower) ||
                 supplierLower.includes(itemSupplierName);
        });
        
        setFilteredInventoryItemsByRow(prev => ({
          ...prev,
          [itemId]: filteredResults
        }));
      } catch (error) {
        console.error('Error searching SKUs:', error);
        setFilteredInventoryItemsByRow(prev => ({
          ...prev,
          [itemId]: []
        }));
      } finally {
        setIsLoadingInventory(false);
      }
    }, 300),
    [supplier, supplierId]
  );

  // Update effect for sku search terms
  useEffect(() => {
    // When search terms change, trigger backend search
    Object.entries(skuSearchTerms).forEach(([itemId, searchTerm]) => {
      if (openSKUDropdowns[itemId] && searchTerm?.trim()) {
        debouncedSearchSKUs(searchTerm, itemId);
      }
    });
  }, [skuSearchTerms, openSKUDropdowns, debouncedSearchSKUs]);

  // Add effect to recalculate totals whenever items change
  useEffect(() => {
    if (!isLoadingFromInvoice) {
      calculateTotals();
    }
  }, [items, isLoadingFromInvoice]);

  // Check authentication first - FIXED: Only run once on mount
  useEffect(() => {
    // Ensure development authentication is set up
    if (!ensureDevelopmentAuth()) {
      console.log('User not authenticated, redirecting to login...');
      navigate('/login');
      return;
    }
    console.log('🔐 Authentication check passed');
  }, []); // Empty dependency array - only run once on mount

  // Function to get filtered inventory items
  const getFilteredInventoryItems = (itemId: string) => {
    const searchTerm = skuSearchTerms[itemId] || '';
    
    // If no supplier is selected, return empty array
    if (!supplier || !supplierId) {
      return [];
    }
    
    // Return filtered inventory items from state
    return filteredInventoryItemsByRow[itemId] || [];
  };

  // FIXED: Enhanced fetchPOData to include BWA pricing
  useEffect(() => {
    const fetchPOData = async () => {
      if (isEditMode && poId) {
        setLoadingPO(true);
        try {
          const poData = await purchaseOrderService.getPurchaseOrderById(parseInt(poId));
          
          // Only allow editing of draft POs
          if (poData.status !== 'draft') {
            toast.showToast('Only draft purchase orders can be edited', { type: 'error' });
            navigate('/po-management');
            return;
          }
          
          // Cast to any to avoid TypeScript errors with optional properties
          const poDataAny = poData as any;
          
          // Set form data from PO
          setStoreType(poDataAny.store_type || '');
          setIssueDate(poDataAny.date || poDataAny.issue_date || new Date().toISOString().split('T')[0]);
          setSupplierId(poData.supplier_id.toString());
          setSupplier(poData.supplier_name || '');
          
          // Set invoice ID if available
          if (poDataAny.invoice_id) {
            skipNextInvoiceFetchRef.current = true;
            setInvoiceId(poDataAny.invoice_id.toString());
          } else if (poDataAny.notes && poDataAny.notes.includes('Auto-generated from Invoice')) {
            // Fallback: try to extract INV number from notes if invoice_id is missing
            // Matches formats like INV-012, INV-PO-012, etc.
            const match = poDataAny.notes.match(/Invoice\s+([A-Z0-9-]+)/i);
            if (match && match[1]) {
              console.log('Parsed invoice ID from notes:', match[1]);
              skipNextInvoiceFetchRef.current = true;
              setInvoiceId(match[1]);
            }
          }
          
          // Set items with BWA pricing
          if (Array.isArray(poData.details) && poData.details.length > 0) {
            const formattedItems = await Promise.all(
              poData.details.map(async (detail) => {
                const sku = detail.sku || '';
                let bwaPrice = 0;
                
                // Fetch BWA pricing for each existing item
                if (sku) {
                  try {
                    const pricingResult = await fetchBWAPricing(sku);
                    bwaPrice = pricingResult.price;
                  } catch (error) {
                    console.warn(`Error fetching BWA pricing for SKU ${sku}:`, error);
                  }
                }
                
                return {
              id: uuidv4(),
                  sku: sku,
              description: detail.description || '',
              quantityOrdered: detail.quantity_ordered || detail.quantity || 0,
              expectedDeliveryDate: detail.expected_delivery_date || '',
                  total: bwaPrice ? calculateItemTotal(detail.quantity_ordered || detail.quantity || 0, bwaPrice) : (detail.total || detail.total_price || 0),
                  notes: detail.notes || '',
                  bwaBuyPrice: bwaPrice
                };
              })
            );
            
            setItems(formattedItems);
            
            // Calculate totals
            let total = 0;
            formattedItems.forEach(item => {
              total += Number(item.total) || 0;
            });
            setTotalAmount(total);
            setGstAmount(total * 0.1);
            setTotalPayable(total * 1.1);
          } else {
            // If no items, create default item with BWA pricing field
            setItems([{
              id: uuidv4(),
              sku: '',
              description: '',
              quantityOrdered: '',
              expectedDeliveryDate: '',
              total: 0,
              notes: '',
              bwaBuyPrice: 0
            }]);
          }
          
        } catch (error) {
          console.error('Error fetching PO data:', error);
          toast.showToast('Failed to load purchase order data', { type: 'error' });
          navigate('/po-management');
        } finally {
          setLoadingPO(false);
        }
      }
    };
    
    fetchPOData();
  }, [isEditMode, poId, navigate]);

  // FIXED INVOICE FETCHING - Use proper API service with authentication and timeout
  const fetchInvoices = useCallback(async (search: string = '') => {
    if (!isMountedRef.current) return;
    
    console.log('🔄 fetchInvoices called with search:', search);
    console.log('🔐 Authentication check:', isAuthenticated());
    console.log('Store filter applied:', storeFilter);
    
    try {
      // Check if user is authenticated
      if (!isAuthenticated()) {
        console.log('❌ User not authenticated');
        return;
      }
      
      console.log('🚀 Starting invoice fetch...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );
      
      const fetchPromise = getInvoices(
        1, // page
        50, // limit
        search ? { search } : {}, // filters
        storeFilter // store filter
      );
      
      const response = await Promise.race([fetchPromise, timeoutPromise]) as any;
      
      console.log('📦 Invoice API response:', response);
      console.log('Store filter was applied:', storeFilter ? `Store: ${storeFilter.userStoreType}, Should Filter: ${storeFilter.shouldFilterByStore}` : 'No store filter');
      
      if (!isMountedRef.current) return;
      
      let processedInvoices: SimpleInvoice[] = [];
      
      // Handle both response.invoices and response.data for backward compatibility
      const invoiceData = response?.invoices || response?.data || response?.items || [];
      
      console.log('📋 Processing invoice data:', invoiceData);
      console.log(`📊 Total invoices received: ${Array.isArray(invoiceData) ? invoiceData.length : 0}`);
      
      if (Array.isArray(invoiceData)) {
        processedInvoices = invoiceData.map((invoice: any, index: number) => {
          const id = invoice.id || `invoice_${index}`;
          
          // Debug: Log store information for first few invoices
          if (index < 3) {
            console.log(`📋 Invoice ${index + 1}: ${invoice.invoice_no || invoice.invoice_number?.formatted_number || id}`, {
              store_type_id: invoice.store_type_id,
              store_type_name: invoice.store_type_name,
              store_type: invoice.store_type
            });
          }
          
          // Create display text - Prioritize invoice_no over invoice_number
          let display_text: string;
          
          // Priority order: invoice_no -> invoice_number.formatted_number -> invoice_number -> id
          if (invoice.invoice_no && typeof invoice.invoice_no === 'string') {
            display_text = invoice.invoice_no;
          } else if (invoice.invoice_number) {
            if (typeof invoice.invoice_number === 'object' && invoice.invoice_number.formatted_number) {
              display_text = invoice.invoice_number.formatted_number;
            } else if (typeof invoice.invoice_number === 'string') {
              display_text = invoice.invoice_number;
            } else {
              display_text = id.toString();
            }
          } else if (invoice.formatted_number) {
            // Fallback to formatted_number if available
            if (typeof invoice.formatted_number === 'object' && invoice.formatted_number.formatted_number) {
              display_text = invoice.formatted_number.formatted_number;
            } else if (typeof invoice.formatted_number === 'string') {
              display_text = invoice.formatted_number;
            } else {
              display_text = id.toString();
            }
          } else {
            display_text = id.toString();
          }
          
          // Add supplier info if available
          if (invoice.supplier_name && typeof invoice.supplier_name === 'string') {
            display_text = `${display_text} - ${invoice.supplier_name}`;
          }
          
          // Debug logging to catch any remaining object issues
          if (typeof display_text !== 'string') {
            console.warn('⚠️ Invoice display_text is not a string:', display_text, 'for invoice:', invoice);
            display_text = String(display_text || id);
          }
          
          return {
            id: id.toString(),
            display_text,
            invoice_number: invoice.invoice_number,
            formatted_number: invoice.formatted_number,
            invoice_no: invoice.invoice_no // Add invoice_no for reference
          };
        });
        
        console.log('Processed invoices:', processedInvoices.length, 'items');
        console.log('Store filtering results:', storeFilter ? 
          `Expected only ${storeFilter.userStoreType} store invoices` : 
          'No store filtering applied (Admin user)'
        );
      } else {
        console.log('⚠️ Invalid invoice data format:', typeof invoiceData);
      }
      
      setInvoices(processedInvoices);
      console.log('Invoices state updated with', processedInvoices.length, 'items');
      
    } catch (error: any) {
      console.error('❌ Error in fetchInvoices:', error.message || error);
      if (!isMountedRef.current) return;
      setInvoices([]); // Ensure invoices is always an array
    }
  }, [storeFilter]);

  // FIXED SUPPLIER FETCHING - Use proper API service with authentication and timeout
  const fetchSuppliers = useCallback(async (search: string = '') => {
    if (!isMountedRef.current) return;
    
    console.log('🔄 fetchSuppliers called with search:', search);
    
    // Check authentication first
    if (!ensureDevelopmentAuth()) {
      console.error('❌ User not authenticated, cannot fetch suppliers');
      toast.showToast('Please log in to access suppliers', { type: 'error' });
      return;
    }
    
    const currentToken = getAuthToken();
    console.log('🔐 Authentication check passed, token:', currentToken ? 'present' : 'missing');
    console.log('🔗 API_URL:', API_URL);
    console.log('🔗 ENDPOINTS.SUPPLIERS:', ENDPOINTS.SUPPLIERS);
    
    try {
      console.log('🚀 Starting supplier fetch...');
      
      const supplierParams = { 
        skip: 0, 
        limit: 50, 
        is_active: true,
        search: search || undefined
      };
      
      console.log('📋 Supplier params:', supplierParams);
      
      // Use the supplier service which handles authentication properly
      const response = await supplierService.getSuppliers(supplierParams);
      
      console.log('📦 Supplier service response received:', response);
      console.log('📦 Response type:', typeof response);
      console.log('📦 Response keys:', response ? Object.keys(response) : 'null/undefined');
      
      if (!isMountedRef.current) return;
      
      let processedSuppliers: SimpleSupplier[] = [];
      
      // The supplier service returns { items: [...], total: number, page: number, limit: number }
      const supplierData = response?.items || [];
      
      console.log('📋 Processing supplier data:', supplierData);
      console.log('📋 Supplier data type:', typeof supplierData);
      console.log('📋 Is supplier data array:', Array.isArray(supplierData));
      console.log('📋 Supplier data length:', Array.isArray(supplierData) ? supplierData.length : 'N/A');
      
      if (Array.isArray(supplierData)) {
        processedSuppliers = supplierData.map((supplier: any, index: number) => {
          console.log(`📋 Processing supplier ${index}:`, supplier);
          
          const id = supplier.id || `supplier_${index}`;
          
          // Ensure display_text is always a string, not an object
          let display_text: string;
          
          if (supplier.supplier_name && typeof supplier.supplier_name === 'string') {
            display_text = supplier.supplier_name;
          } else if (supplier.name && typeof supplier.name === 'string') {
            display_text = supplier.name;
          } else {
            display_text = `Supplier ${id}`;
          }
          
          // Debug logging to catch any remaining object issues
          if (typeof display_text !== 'string') {
            console.warn('⚠️ Supplier display_text is not a string:', display_text, 'for supplier:', supplier);
            display_text = String(display_text || `Supplier ${id}`);
          }
          
          const processedSupplier = {
            id: id.toString(),
            display_text,
            supplier_name: supplier.supplier_name,
            name: supplier.name
          };
          
          console.log(`Processed supplier ${index}:`, processedSupplier);
          return processedSupplier;
        });
        
        console.log('All processed suppliers:', processedSuppliers);
      } else {
        console.log('⚠️ Invalid supplier data format:', typeof supplierData);
        console.log('⚠️ Supplier data value:', supplierData);
      }
      
      console.log('Setting suppliers state with', processedSuppliers.length, 'items');
      setSuppliers(processedSuppliers);
      console.log('Suppliers state updated successfully');
      
      // Show success message if suppliers were loaded
      if (processedSuppliers.length > 0) {
        console.log(`Successfully loaded ${processedSuppliers.length} suppliers`);
        // Removed success toast notification - not needed on page load
        // toast.showToast(`Loaded ${processedSuppliers.length} suppliers`, { type: 'success' });
      } else {
        console.log('⚠️ No suppliers found, trying fallback endpoint...');
        
        // Try the /names endpoint as fallback
        try {
          const token = getAuthToken();
          const fallbackResponse = await fetch(`${API_URL}${ENDPOINTS.SUPPLIERS}names`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          
          if (fallbackResponse.ok) {
            const namesData = await fallbackResponse.json();
            console.log('📦 Fallback supplier names response:', namesData);
            
            if (Array.isArray(namesData)) {
              const fallbackSuppliers = namesData.map((supplier: any) => ({
                id: supplier.id.toString(),
                display_text: supplier.supplier_name || supplier.name || `Supplier ${supplier.id}`,
                supplier_name: supplier.supplier_name,
                name: supplier.name
              }));
              
              setSuppliers(fallbackSuppliers);
              console.log(`Loaded ${fallbackSuppliers.length} suppliers from fallback endpoint`);
              // Removed success toast notification - not needed on page load
              // toast.showToast(`Loaded ${fallbackSuppliers.length} suppliers`, { type: 'success' });
              return;
            }
          }
        } catch (fallbackError) {
          console.error('❌ Fallback endpoint also failed:', fallbackError);
        }
        
        // Keep the info toast for when no suppliers are found as this is important
        toast.showToast('No suppliers found', { type: 'info' });
      }
      
    } catch (error: any) {
      console.error('❌ Error in fetchSuppliers:', error);
      console.error('❌ Error message:', error.message || 'Unknown error');
      console.error('❌ Error stack:', error.stack);
      
      if (!isMountedRef.current) return;
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to load suppliers';
      if (error.message && error.message.includes('Authentication failed')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.message && error.message.includes('Network error')) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.message && error.message.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again.';
      }
      
      toast.showToast(errorMessage, { type: 'error' });
      setSuppliers([]); // Ensure suppliers is always an array
    }
  }, [toast]);
      
  // Improved inventory fetching function with timeout - now loads minimal set
  const fetchInventoryWithTimeout = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setIsLoadingInventory(true);
    
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );
      
      // Just load a minimal set of items for initial display
      // Full search will be handled by the backend search
      const fetchPromise = inventoryService.getInventoryItems({ limit: 20, active_only: true });
      const response = await Promise.race([fetchPromise, timeoutPromise]) as any;
      
      if (!isMountedRef.current) return;
      
      if (response && response.length > 0) {
        console.log('Fetched minimal inventory item set:', response.length);
        setInventoryItems(response);
      } else {
        console.warn('Failed to fetch initial inventory items');
        setInventoryItems([]);
      }
    } catch (error: any) {
      if (isMountedRef.current) {
        if (error.message !== 'Request timeout') {
          console.error('Failed to load inventory:', error);
        }
        setInventoryItems([]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingInventory(false);
      }
    }
  }, []);

  // Function to fetch invoice details and populate SKUs
  const fetchInvoiceDetails = async (selectedInvoiceId: string) => {
    if (!isMountedRef.current || !selectedInvoiceId) return;
    
    try {
      // Set loading states
      setIsLoadingFromInvoice(true);
      setIsLoadingInvoiceDetails(true);
      
      // Create a new AbortController for this request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      const response = await fetch(`${API_URL}/api/v1/invoices/${selectedInvoiceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch invoice details: ${response.status}`);
      }
      
      const invoiceDetails = await response.json();
      
      if (!isMountedRef.current) return;
      
      console.log('📋 Invoice details received:', invoiceDetails);
      
      // Pre-fill Store Type from invoice details
      if (invoiceDetails.store_type) {
        const storeTypeName = invoiceDetails.store_type.name || invoiceDetails.store_type;
        if (storeTypeName && typeof storeTypeName === 'string') {
          console.log(`Pre-filling Store Type: ${storeTypeName}`);
          setStoreType(storeTypeName);
        }
      }
      
      // Extract items from the invoice - handle both direct array and nested structure
      let invoiceItems = [];
      if (invoiceDetails.items) {
        if (Array.isArray(invoiceDetails.items)) {
          invoiceItems = invoiceDetails.items;
        } else if (invoiceDetails.items.items && Array.isArray(invoiceDetails.items.items)) {
          // Handle nested structure like {"items": [...]}
          invoiceItems = invoiceDetails.items.items;
        }
      }
      
      // Track the matched supplier ID for filtering
      let matchedSupplierId = supplierId;

      // Pre-fill Supplier from invoice items (get supplier_name from first item)
      if (invoiceItems.length > 0) {
        const firstItem = invoiceItems[0];
        const supplierName = firstItem.supplier_name || firstItem.supplier?.name || firstItem.supplier?.supplier_name;
        
        // If we don't have a supplier selected, use the first item's supplier_id
        if (!matchedSupplierId && firstItem.supplier_id) {
          matchedSupplierId = firstItem.supplier_id.toString();
        }
        
        if (supplierName && typeof supplierName === 'string') {
          console.log(`🏭 Pre-filling Supplier: ${supplierName}`);
          
          // Helper function to match and set supplier
          const matchAndSetSupplier = (suppliersList: SimpleSupplier[]) => {
            const matchingSupplier = suppliersList.find(s => {
              const supplierDisplayName = s.supplier_name || s.name || s.display_text;
              return supplierDisplayName && supplierDisplayName.toLowerCase() === supplierName.toLowerCase();
            });
            
            if (matchingSupplier) {
              matchedSupplierId = matchingSupplier.id.toString();
              setSupplierId(matchingSupplier.id.toString());
              setSupplier(matchingSupplier.display_text || matchingSupplier.supplier_name || matchingSupplier.name || supplierName);
              console.log(`Found matching supplier: ${matchingSupplier.display_text} (ID: ${matchingSupplier.id})`);
              return true;
            }
            return false;
          };
          
          // Try to match with current suppliers list
          if (suppliers.length > 0) {
            if (!matchAndSetSupplier(suppliers)) {
              // Supplier not found, set name only
              console.log(`⚠️ Supplier "${supplierName}" not found in suppliers list, setting name only`);
              setSupplier(supplierName);
            }
          } else {
            // Suppliers not loaded yet, fetch them first
            console.log(`📥 Suppliers not loaded, fetching suppliers first...`);
            setSupplier(supplierName); // Set name temporarily
            
            fetchSuppliers().then(() => {
              // Use a small timeout to ensure state is updated
              setTimeout(() => {
                // Get fresh suppliers from state by using a callback
                // Since we can't directly access updated state, we'll use a different approach
                // Re-fetch suppliers and match
                supplierService.getSuppliers({ skip: 0, limit: 100, is_active: true }).then((response: any) => {
                  const supplierData = response?.items || [];
                  const processedSuppliers: SimpleSupplier[] = supplierData.map((s: any) => ({
                    id: s.id.toString(),
                    display_text: s.supplier_name || s.name || `Supplier ${s.id}`,
                    supplier_name: s.supplier_name,
                    name: s.name
                  }));
                  
                  if (matchAndSetSupplier(processedSuppliers)) {
                    console.log(`Supplier matched after fetching suppliers list`);
                  }
                }).catch((error) => {
                  console.error('Error fetching suppliers for matching:', error);
                });
              }, 100);
            });
          }
        }
      }
      
      // We do NOT filter invoiceItems here anymore! They will be filtered later when updating the items state.
      
      // Check if invoice has items to populate
      if (invoiceItems.length > 0) {
        // Map invoice items to PO items with proper total calculation
        const poItems = invoiceItems.map((item: any) => {
          // Calculate quantity ordered from invoice item quantities
          // Use the correct field names: quantity_m2, quantity_boxes, quantity_units, quantity_pieces
          let quantityOrdered = 0;
          
          // Sum up all quantity fields - prioritize new field names, fallback to old ones for backward compatibility
          if (item.quantity_units) {
            quantityOrdered += parseFloat(item.quantity_units) || 0;
          } else if (item.units) {
            quantityOrdered += parseFloat(item.units) || 0;
          }
          
          if (item.quantity_boxes) {
            quantityOrdered += parseFloat(item.quantity_boxes) || 0;
          } else if (item.boxes) {
            quantityOrdered += parseFloat(item.boxes) || 0;
          }
          
          if (item.quantity_pieces) {
            quantityOrdered += parseFloat(item.quantity_pieces) || 0;
          } else if (item.pieces) {
            quantityOrdered += parseFloat(item.pieces) || 0;
          }
          
          if (item.quantity_m2) {
            quantityOrdered += parseFloat(item.quantity_m2) || 0;
          } else if (item.m2) {
            quantityOrdered += parseFloat(item.m2) || 0;
          }
          
          // If no quantities found, try to use a generic quantity field or total_quantity
          if (quantityOrdered === 0) {
            if (item.total_quantity) {
              quantityOrdered = parseFloat(item.total_quantity) || 0;
            } else if (item.quantity) {
              quantityOrdered = parseFloat(item.quantity) || 0;
            }
          }
          
          // Extract total price - use the correct field names from backend
          let itemTotal = 0;
          if (item.totalPrice) {
            // This is the main field used in the backend
            itemTotal = parseFloat(item.totalPrice) || 0;
          } else if (item.total_price) {
            itemTotal = parseFloat(item.total_price) || 0;
          } else if (item.total) {
            itemTotal = parseFloat(item.total) || 0;
          } else if (item.unit_price && quantityOrdered > 0) {
            // Calculate total from unit price and quantity
            itemTotal = (parseFloat(item.unit_price) || 0) * quantityOrdered;
          }
          
          // Extract SKU - check multiple possible field names to ensure we get the SKU code
          // Prioritize sku_code, but also check other common field names
          let itemSku = '';
          if (item.sku_code && typeof item.sku_code === 'string') {
            itemSku = item.sku_code;
          } else if (item.sku && typeof item.sku === 'string') {
            itemSku = item.sku;
          } else if (item.bwa_sku && typeof item.bwa_sku === 'string') {
            itemSku = item.bwa_sku;
          } else if (item.code && typeof item.code === 'string') {
            itemSku = item.code;
          }
          
          // Log the item structure for debugging if SKU is missing
          if (!itemSku) {
            console.warn(`⚠️ No SKU found for item:`, {
              availableFields: Object.keys(item),
              item: item
            });
          }
          
          console.log(`📦 Item SKU: ${itemSku}, Quantity: ${quantityOrdered}, Total: ${itemTotal}`);
          
          return {
            id: uuidv4(),
            sku: itemSku,
            description: item.description || '',
            quantityOrdered: quantityOrdered || '',
            expectedDeliveryDate: '',
            total: itemTotal,
            notes: '',
            supplierId: item.supplier_id ? item.supplier_id.toString() : ''
          };
        });
        
        // Store all raw items from invoice
        setAllInvoiceItems(poItems);
        
        // Filter items for the currently matched supplier
        let filteredItems = poItems;
        if (matchedSupplierId) {
           filteredItems = poItems.filter((item: POItem) => item.supplierId === matchedSupplierId);
           console.log(`Filtered invoice items to ${filteredItems.length} using supplier ${matchedSupplierId}`);
        } else {
           filteredItems = []; // No supplier matched, start empty
        }
        
        // Update items state with the filtered invoice items
        if (filteredItems.length > 0) {
          setItems(filteredItems);
        } else {
          setItems([{
            id: uuidv4(),
            sku: '',
            description: '',
            quantityOrdered: '',
            expectedDeliveryDate: '',
            total: 0,
            notes: ''
          }]);
        }
        
        // Initialize SKU search terms for all items so they display correctly
        const newSkuSearchTerms: { [key: string]: string } = {};
        (filteredItems.length > 0 ? filteredItems : poItems).forEach((item: POItem) => {
          if (item.sku) {
            newSkuSearchTerms[item.id] = item.sku;
          }
        });
        setSKUSearchTerms(newSkuSearchTerms);
        
        // Set the grand total from invoice
        if (invoiceDetails.grand_total) {
          const grandTotal = parseFloat(invoiceDetails.grand_total) || 0;
          console.log(`Setting grand total from invoice: ${grandTotal}`);
          setTotalAmount(grandTotal);
          
          // Calculate GST (10% of total) - but check if GST is already included
          let gstAmount = 0;
          if (invoiceDetails.total_gst) {
            // Use the GST amount from the invoice
            gstAmount = parseFloat(invoiceDetails.total_gst) || 0;
          } else {
            // Calculate 10% GST
            gstAmount = grandTotal * 0.1;
          }
          setGstAmount(gstAmount);
          
          // Total payable - if grand_total already includes GST, use it as is
          // Otherwise add GST to get total payable
          if (invoiceDetails.total_gst) {
            // Grand total likely already includes GST
            setTotalPayable(grandTotal);
          } else {
            // Add GST to grand total
            setTotalPayable(grandTotal + gstAmount);
          }
        } else if (invoiceDetails.total_order) {
          // Fallback to total_order if grand_total not available
          const totalOrder = parseFloat(invoiceDetails.total_order) || 0;
          console.log(`Setting total from total_order: ${totalOrder}`);
          setTotalAmount(totalOrder);
          
          const gstAmount = totalOrder * 0.1;
          setGstAmount(gstAmount);
          setTotalPayable(totalOrder + gstAmount);
        }
        
        console.log(`Successfully populated ${poItems.length} items from invoice ${selectedInvoiceId}`);
        toast.showToast(`Successfully loaded ${poItems.length} items from invoice`, { type: 'success' });
      } else {
        console.log('⚠️ No items found in invoice or invalid invoice structure');
        toast.showToast('No items found in the selected invoice', { type: 'warning' });
      }
      
    } catch (error: any) {
      // Don't show error toast if request was aborted (this is intentional when user changes selection)
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('ℹ️ Invoice details request was aborted (likely due to new selection)');
        return;
      }
      
      // Only show error if component is still mounted and we have a valid invoice ID
      // This prevents showing errors for stale requests
      if (isMountedRef.current && selectedInvoiceId === invoiceId) {
        console.error('❌ Error fetching invoice details:', error.message || error);
        toast.showToast('Failed to load invoice details', { type: 'error' });
      }
    } finally {
      // Reset the flag after loading is complete
      if (isMountedRef.current) {
        setIsLoadingFromInvoice(false);
        setIsLoadingInvoiceDetails(false);
      }
    }
  };

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Effect to fetch invoice details when invoice ID changes
  useEffect(() => {
    if (invoiceId) {
      if (skipNextInvoiceFetchRef.current) {
        console.log(`Skipping invoice fetch for ${invoiceId} on initial PO load`);
        skipNextInvoiceFetchRef.current = false;
        return;
      }
      console.log(`Invoice ID changed to ${invoiceId}, fetching details...`);
      fetchInvoiceDetails(invoiceId);
    }
  }, [invoiceId]);

  // SIMPLIFIED INITIALIZATION - Load data in background, show form immediately
  useEffect(() => {
    console.log('🚀 CreatePOPage initialization started');
    console.log('Store filter:', storeFilter);
      
    // Set default store type for restricted users
    if (storeFilter.isStoreRestricted && storeFilter.userStoreType) {
      console.log('🔒 Setting default store type:', storeFilter.userStoreType);
      setStoreType(storeFilter.userStoreType);
    }

    // Load real data from APIs
    if (!initializationRef.current) {
      initializationRef.current = true;
      console.log('💾 Starting data loading...');
      
      // Load real data from APIs
      fetchInvoices().catch(err => console.warn('Failed to load invoices:', err));
      fetchSuppliers().catch(err => console.warn('Failed to load suppliers:', err));
      
      // Load inventory with better error handling
      fetchInventoryWithTimeout().then(() => {
        console.log(`Inventory loaded: ${inventoryItems.length} items`);
      }).catch(err => {
        console.warn('Failed to load inventory:', err);
        // Removed warning toast notification - not needed on page load, just log the warning
        // toast.showToast('Failed to load inventory items. SKU dropdown may not work properly.', { type: 'warning' });
      });
    } else {
      console.log('⏭️ Data loading already initialized, skipping...');
    }
  }, []); // Empty dependency array to prevent infinite loops

  // Debug effect to monitor inventory loading
  useEffect(() => {
    console.log(`📊 Inventory items updated: ${inventoryItems.length} items`);
    if (inventoryItems.length > 0) {
      console.log('📋 Sample inventory items:', inventoryItems.slice(0, 3));
    }
  }, [inventoryItems]);

  const calculateTotals = () => {
    const total = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const gst = total * 0.1; // 10% GST
    const payable = total + gst;

    // Round all values to 2 decimal places for consistent display
    const roundedTotal = Math.round(total * 100) / 100;
    const roundedGst = Math.round(gst * 100) / 100;
    const roundedPayable = Math.round(payable * 100) / 100;

    setTotalAmount(roundedTotal);
    setGstAmount(roundedGst);
    setTotalPayable(roundedPayable);
    return roundedTotal;
  };

  // Function to fetch BWA BUY PRICE for a SKU
  const fetchBWAPricing = async (sku: string): Promise<{ price: number; notes: string }> => {
    if (!sku || bwaPricing[sku] !== undefined) {
      return { price: bwaPricing[sku] || 0, notes: '' };
    }

    setIsLoadingBWAPricing(prev => ({ ...prev, [sku]: true }));

    try {
      console.log(`Fetching BWA BUY PRICE for SKU: ${sku}`);
      const pricingResponse = await supplierService.getSkuPricing(sku);
      
      let bwaPrice = 0;
      let notes = '';
      if (pricingResponse.success) {
        // First try to get bwa_buy_price_50_ex_gst from the response
        if (pricingResponse.bwa_buy_price_50_ex_gst) {
          bwaPrice = parseFloat(pricingResponse.bwa_buy_price_50_ex_gst.toString()) || 0;
        } else {
          // For debugging - log the response structure
          console.log('BWA BUY PRICE response:', pricingResponse);
        }
        // Get notes from the response
        if (pricingResponse.notes) {
          notes = pricingResponse.notes;
        }
      } else {
        console.warn(`No pricing data found for SKU: ${sku}`);
      }

      console.log(`BWA BUY PRICE for SKU ${sku}: $${bwaPrice}`);
      setBwaPricing(prev => ({ ...prev, [sku]: bwaPrice }));
      return { price: bwaPrice, notes };
    } catch (error) {
      console.error(`Error fetching BWA pricing for SKU ${sku}:`, error);
      setBwaPricing(prev => ({ ...prev, [sku]: 0 }));
      return { price: 0, notes: '' };
    } finally {
      setIsLoadingBWAPricing(prev => ({ ...prev, [sku]: false }));
    }
  };

  // Function to calculate total based on quantity and BWA BUY PRICE
  const calculateItemTotal = (quantityOrdered: string | number, bwaPrice: number): number => {
    const quantity = typeof quantityOrdered === 'string' ? parseFloat(quantityOrdered) || 0 : quantityOrdered || 0;
    const total = quantity * bwaPrice;
    // Round to 2 decimal places for consistency
    return Math.round(total * 100) / 100;
  };

  // Enhanced SKU selection handler with BWA pricing
  const handleSKUSelect = async (itemId: string, inventoryItem: any) => {
    const sku = inventoryItem.sku_code;
    
    console.log(`🔍 SKU selected: ${sku} for item ${itemId}`);
    
    // Update the item with selected SKU
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? {
          ...item,
          sku: sku,
          description: inventoryItem.style_code || inventoryItem.description || '',
        } : item
      )
    );

    // Update search term to show the selected SKU and close dropdown
    setSKUSearchTerms(prev => ({
      ...prev,
      [itemId]: sku
    }));
    setOpenSKUDropdowns(prev => ({
      ...prev,
      [itemId]: false
    }));

    // Fetch BWA BUY PRICE and update total
    console.log(`Fetching BWA pricing for SKU: ${sku}`);
    const { price: bwaPrice, notes } = await fetchBWAPricing(sku);
    
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            bwaBuyPrice: bwaPrice,
            total: calculateItemTotal(item.quantityOrdered, bwaPrice),
            notes: notes || item.notes || ''
          };
          console.log(`Updated item with BWA price $${bwaPrice}, total: $${updatedItem.total}, notes: ${notes ? 'yes' : 'no'}`);
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleAddItem = () => {
    setItems([...items, {
      id: uuidv4(),
      sku: '',
      description: '',
      quantityOrdered: '',
      expectedDeliveryDate: '',
      total: 0,
      notes: '',
      bwaBuyPrice: 0 // Initialize with BWA pricing field
    }]);
  };

  const handleDeleteItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  // Enhanced item change handler to auto-calculate totals
  const handleItemChange = (id: string, field: keyof POItem, value: any) => {
    setItems(prevItems =>
      prevItems.map(item => {
        if (item.id === id) {
          let processedValue = value;
          
          // Clean SKU format when SKU field is updated
          if (field === 'sku') {
            processedValue = cleanSKU(value);
          }
          
          const updatedItem = { ...item, [field]: processedValue };
          
          // Auto-calculate total when quantity changes
          if (field === 'quantityOrdered' && updatedItem.bwaBuyPrice) {
            updatedItem.total = calculateItemTotal(processedValue, updatedItem.bwaBuyPrice);
          }
          
          // If SKU is manually entered, fetch BWA pricing
          if (field === 'sku' && processedValue && processedValue !== item.sku) {
            // Fetch BWA pricing for manually entered SKU
            fetchBWAPricing(processedValue).then(({ price: bwaPrice, notes }) => {
              setItems(currentItems =>
                currentItems.map(currentItem => {
                  if (currentItem.id === id) {
                    return {
                      ...currentItem,
                      bwaBuyPrice: bwaPrice,
                      total: calculateItemTotal(currentItem.quantityOrdered, bwaPrice),
                      notes: notes || currentItem.notes || ''
                    };
                  }
                  return currentItem;
                })
              );
            });
          }
          
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleViewNotes = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      setCurrentNotes(item.notes);
      setCurrentItemId(itemId);
      setShowNotesModal(true);
    }
  };

  const handleSaveNotes = () => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === currentItemId ? { ...item, notes: currentNotes } : item
      )
    );
    setShowNotesModal(false);
    setCurrentNotes('');
    setCurrentItemId('');
  };

  // Enhanced supplier change handler
  const handleSupplierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSupplierId = e.target.value;
    setSupplierId(selectedSupplierId);

    // Find the supplier name for display
    const selectedSupplier = suppliers.find(s => s.id.toString() === selectedSupplierId);
    if (selectedSupplier) {
      setSupplier(selectedSupplier.display_text);
    } else {
      setSupplier('');
    }

    // If an invoice is selected, filter the items based on the new supplier
    if (invoiceId && allInvoiceItems.length > 0) {
      const filteredItems = allInvoiceItems.filter(item => item.supplierId === selectedSupplierId);
      if (filteredItems.length > 0) {
        setItems(filteredItems);
        // Re-initialize SKU search terms for the new filtered items
        const newSkuSearchTerms: { [key: string]: string } = {};
        filteredItems.forEach((item: POItem) => {
          if (item.sku) {
            newSkuSearchTerms[item.id] = item.sku;
          }
        });
        setSKUSearchTerms(newSkuSearchTerms);
        
        // Recalculate totals
        const newTotalAmount = filteredItems.reduce((sum, item) => sum + (item.total || 0), 0);
        setTotalAmount(newTotalAmount);
        setGstAmount(newTotalAmount * 0.1);
        setTotalPayable(newTotalAmount * 1.1);
      } else {
        // Reset to empty item if this supplier has no items in the invoice
        setItems([{
          id: uuidv4(),
          sku: '',
          description: '',
          quantityOrdered: '',
          expectedDeliveryDate: '',
          total: 0,
          notes: ''
        }]);
        setSKUSearchTerms({});
        setTotalAmount(0);
        setGstAmount(0);
        setTotalPayable(0);
      }
    } else {
      // Clear all SKU search terms when supplier changes (not loading from invoice)
      setSKUSearchTerms({});
    }
    
    // Close all open SKU dropdowns
    setOpenSKUDropdowns({});
  };

  const handleStoreTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStoreType(e.target.value);
  };

  // Function to clean SKU format
  const cleanSKU = (sku: string): string => {
    if (!sku) return '';
    
    // Remove extra quotes, escape characters, and trim whitespace
    return sku
      .replace(/^["']+|["']+$/g, '') // Remove leading/trailing quotes
      .replace(/\\"/g, '"') // Replace escaped quotes with regular quotes
      .replace(/\\\\/g, '\\') // Replace double backslashes with single
      .trim(); // Remove leading/trailing whitespace
  };

  // Helper function for automatic email sending
  const handleAutoEmail = async (id: number) => {
    try {
      const emailResponse = await purchaseOrderService.sendPurchaseOrderEmail(id);
      if (emailResponse.success) {
        toast.showToast(`Email sent to supplier: ${emailResponse.supplier_email}`, { type: 'success' });
      }
    } catch (emailError) {
      console.error('Failed to send automatic email:', emailError);
      toast.showToast('PO saved, but failed to send automatic email to supplier.', { type: 'warning' });
    }
  };

  // Handle form submission with proper error handling
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submission
    if (loading) {
      return;
    }

    // Auto-set store type for restricted users if not set
    let finalStoreType = storeType;
    if (!finalStoreType && storeFilter.isStoreRestricted && storeFilter.userStoreType) {
      console.log('Auto-setting store type for restricted user:', storeFilter.userStoreType);
      finalStoreType = storeFilter.userStoreType;
      setStoreType(finalStoreType);
    }

    if (!finalStoreType || !supplierId || !issueDate) {
      toast.showToast('Please fill in all required fields', { type: 'error' });
      return;
    }

    if (items.length === 0 || !items.some(item => item.sku && item.description)) {
      toast.showToast('Please add at least one item with SKU and description', { type: 'error' });
      return;
    }

    setLoading(true);

    const purchaseOrderData = {
      store_type: finalStoreType,
      supplier_id: parseInt(supplierId),
      invoice_id: invoiceId || undefined,
      issue_date: issueDate,
      status: 'issued' as const, // Changed from 'ordered' to 'issued' to match API requirements
      details: items.filter(item => item.sku && item.description).map(item => ({
        sku: cleanSKU(item.sku), // Clean the SKU format
        description: item.description,
        quantity_ordered: parseInt(item.quantityOrdered as string) || 0,
        quantity_received: 0,
        expected_delivery_date: item.expectedDeliveryDate || undefined,
        total: item.total || 0,
        notes: item.notes || ''
      }))
    };

    try {
      if (isEditMode && poId) {
        // Update existing PO
        const finalId = parseInt(poId);
        await dispatch(updatePurchaseOrder({ id: finalId, ...purchaseOrderData })).unwrap();
        toast.showToast('Purchase order updated successfully', { type: 'success' });
        await handleAutoEmail(finalId);
      } else {
        // Create new PO
        const result = await dispatch(createPurchaseOrder(purchaseOrderData)).unwrap() as any;
        const finalId = typeof result.id === 'string' ? parseInt(result.id) : result.id;
        toast.showToast('Purchase order created successfully', { type: 'success' });
        await handleAutoEmail(finalId);
      }
      
      // Reset unsaved changes flag before navigation
      setHasUnsavedChanges(false);
      
      // Small delay to ensure toast is visible before navigation
      setTimeout(() => {
        navigate('/po-management', { state: { refresh: true } });
      }, 500);
    } catch (error: any) {
      console.error('Error creating/updating purchase order:', error);
      const errorMessage = error?.message || error?.data?.detail || `Failed to ${isEditMode ? 'update' : 'create'} purchase order. Please try again.`;
      toast.showToast(errorMessage, { type: 'error' });
      setLoading(false);
    }
  };

  // New function to handle saving as draft
  const handleSaveAsDraft = async () => {
    // Auto-set store type for restricted users if not set
    let finalStoreType = storeType;
    if (!finalStoreType && storeFilter.isStoreRestricted && storeFilter.userStoreType) {
      console.log('Auto-setting store type for restricted user:', storeFilter.userStoreType);
      finalStoreType = storeFilter.userStoreType;
      setStoreType(finalStoreType);
    }

    if (!finalStoreType || !supplierId || !issueDate) {
      toast.showToast('Please fill in all required fields', { type: 'error' });
      return;
    }

    if (items.length === 0 || !items.some(item => item.sku && item.description)) {
      toast.showToast('Please add at least one item with SKU and description', { type: 'error' });
      return;
    }

    setLoading(true);

    const draftPurchaseOrder = {
      store_type: finalStoreType,
      supplier_id: parseInt(supplierId),
      invoice_id: invoiceId || undefined,
      issue_date: issueDate,
      status: 'draft' as const, // Set status as draft
      details: items.filter(item => item.sku && item.description).map(item => ({
        sku: cleanSKU(item.sku), // Clean the SKU format
        description: item.description,
        quantity_ordered: parseInt(item.quantityOrdered as string) || 0,
        quantity_received: 0,
        expected_delivery_date: item.expectedDeliveryDate || undefined,
        total: item.total || 0,
        notes: item.notes || ''
      }))
    };

    try {
      if (isEditMode && poId) {
        // Update existing draft PO
        await dispatch(updatePurchaseOrder({ id: parseInt(poId), ...draftPurchaseOrder })).unwrap();
        toast.showToast('Draft purchase order updated', { type: 'success' });
      } else {
        // Create new draft PO
        await dispatch(createPurchaseOrder(draftPurchaseOrder)).unwrap();
        toast.showToast('Purchase order saved as draft', { type: 'success' });
      }
      navigate('/po-management', { state: { refresh: true } });
    } catch (error) {
      console.error('Error saving purchase order as draft:', error);
      toast.showToast(`Failed to ${isEditMode ? 'update' : 'save'} purchase order as draft. Please try again.`, { type: 'error' });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const hasChanges = hasFormDataChanged();
    
    // If initialFormState is null, check if form has any meaningful data
    if (!initialFormState) {
      const hasAnyData = 
        storeType !== '' ||
        invoiceId !== '' ||
        supplier !== '' ||
        supplierId !== '' ||
        paymentTerms !== 'Net 30' ||
        (items && items.length > 0 && items.some(item => item.sku !== ''));
      
      if (hasAnyData) {
        setShowExitConfirmation(true);
        return;
      }
    }
    
    if (hasChanges) {
      setShowExitConfirmation(true);
    } else {
      setHasUnsavedChanges(false);
      navigate('/po-management');
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    setHasUnsavedChanges(false);
    navigate('/po-management');
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
  };

  // Update context when form changes
  useEffect(() => {
    const hasChanges = hasFormDataChanged();
    setHasUnsavedChanges(hasChanges);
  }, [storeType, invoiceId, issueDate, supplier, supplierId, paymentTerms, items, initialFormState, setHasUnsavedChanges]);

  const formatCurrency = (value: number) => {
    return value.toFixed(2);
  };

  // Close SKU dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.sku-dropdown-container')) {
        setOpenSKUDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Enhanced invoice change handler
  const handleInvoiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedInvoiceId = e.target.value;
    setInvoiceId(selectedInvoiceId);
    
    // If no invoice is selected, reset to default empty item
    if (!selectedInvoiceId) {
      // Reset the flag to allow normal calculation
      setIsLoadingFromInvoice(false);
      setAllInvoiceItems([]);
      
      setItems([{
        id: uuidv4(),
        sku: '',
        description: '',
        quantityOrdered: '',
        expectedDeliveryDate: '',
        total: 0,
        notes: ''
      }]);
      
      // Reset totals to zero
      setTotalAmount(0);
      setGstAmount(0);
      setTotalPayable(0);
    }
    // Note: fetchInvoiceDetails is handled by useEffect when invoiceId changes
    // No need to call it directly here to avoid duplicate calls
  };

  return (
    <>
      <GlobalTableStyle />
      <Layout>
      <PageContainer>
        <PageHeader>
          <HeaderRow>
            <StyledBackButton onClick={handleCancel}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </StyledBackButton>
            <BreadcrumbContainer>
              <BreadcrumbLink to="/po-management">Purchase Orders</BreadcrumbLink>
              <BreadcrumbSeparator>/</BreadcrumbSeparator>
              <PageTitle>{isEditMode ? 'Edit Purchase Order' : 'Create New PO'}</PageTitle>
            </BreadcrumbContainer>
          </HeaderRow>
        </PageHeader>

        {/* Main Content */}
        {loadingPO ? (
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <p>Loading purchase order data...</p>
          </div>
        ) : (
          <div style={{ padding: '0 32px' }}>
            <form onSubmit={handleSubmit}>
              <FormGrid>
                <FormGroup>
                  <FormLabel>Store Type<span className="required">*</span></FormLabel>
                  <StyledFormSelect
                    value={storeType}
                    onChange={handleStoreTypeChange}
                    required
                    disabled={storeFilter.isStoreRestricted}
                    style={{
                      backgroundColor: storeFilter.isStoreRestricted ? '#F9FAFB' : 'white',
                      cursor: storeFilter.isStoreRestricted ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">
                      {storeFilter.isStoreRestricted && storeFilter.userStoreType
                        ? storeFilter.userStoreType
                        : 'Select a store type'
                      }
                    </option>
                    <option value="Cranbourne">Cranbourne</option>
                    <option value="Sale">Sale</option>
                  </StyledFormSelect>
                  {storeFilter.isStoreRestricted && (
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Store type is auto-selected based on your role
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Link to Invoice</FormLabel>
                  <StyledFormSelect
                    value={invoiceId}
                    onChange={handleInvoiceChange}
                  >
                    <option value="">Select Invoice</option>
                    {invoiceId && !invoices.find(inv => inv.id.toString() === invoiceId.toString()) && (
                      <option value={invoiceId}>{invoiceId}</option>
                    )}
                    {invoices && invoices.length > 0 && invoices.map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {typeof invoice.display_text === 'string' ? invoice.display_text : String(invoice.display_text || invoice.id)}
                      </option>
                    ))}
                  </StyledFormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Date of Issued<span className="required">*</span></FormLabel>
                  <DateInputWrapper>
                    <FormInput
                      type="date"
                      value={issueDate}
                      onChange={(e) => setIssueDate(e.target.value)}
                      required
                    />
                    <svg 
                      className="calendar-icon" 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#6B7280" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </DateInputWrapper>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Supplier<span className="required">*</span></FormLabel>
                  <StyledFormSelect
                    value={supplierId}
                    onChange={handleSupplierChange}
                    required
                  >
                    <option value="">Select Supplier</option>
                    {supplierId && !suppliers.find(s => s.id.toString() === supplierId.toString()) && (
                      <option value={supplierId}>{supplier || 'Loading supplier...'}</option>
                    )}
                    {suppliers && suppliers.length > 0 && suppliers
                      .filter(supplierObj => {
                        // If invoice is selected, only show suppliers that have items in the invoice
                        if (invoiceId && allInvoiceItems.length > 0) {
                          return allInvoiceItems.some(item => item.supplierId === supplierObj.id.toString());
                        }
                        return true;
                      })
                      .map((supplierObj) => (
                      <option key={supplierObj.id} value={supplierObj.id}>
                        {typeof supplierObj.display_text === 'string' ? supplierObj.display_text : String(supplierObj.display_text || supplierObj.id)}
                      </option>
                    ))}
                  </StyledFormSelect>
                </FormGroup>
              </FormGrid>

              <TableContainer>
                <Table>
                  <colgroup>
                    <col style={{ width: '50px' }} />
                    <col />
                    <col />
                    <col style={{ width: '125px' }} />
                    <col style={{ width: '190px' }} />
                    <col style={{ width: '250px' }} />
                    <col style={{ width: '80px' }} />
                  </colgroup>
                  <TableHeader>
                    <tr>
                      <th className="notes-column-header">
                        Notes
                      </th>
                      <th>SKU</th>
                      <th>Description</th>
                      <th>Quantity Ordered</th>
                      <th>Expected Delivery Date for Backorder</th>
                      <th>Total (ex GST)</th>
                      <th></th>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td className="notes-column-cell">
                          <NotesIcon 
                            onClick={() => {
                              if (item.sku && item.sku.trim().length > 0) {
                                handleViewNotes(item.id);
                              }
                            }}
                            style={{ 
                              filter: item.notes && item.notes.length > 0 ? 'invert(15%) sepia(95%) saturate(6932%) hue-rotate(358deg) brightness(95%) contrast(112%)' : 'none',
                              cursor: item.sku && item.sku.trim().length > 0 ? 'pointer' : 'not-allowed',
                              opacity: item.sku && item.sku.trim().length > 0 ? 1 : 0.5
                            }}
                          >
                            <img src={poManagementNotesIcon} alt="Notes" />
                          </NotesIcon>
                        </td>
                        <td className="wrap">
                          <SKUSelectWrapper className="sku-dropdown-container">
                            {item.sku ? (
                              <SelectedSKUContainer>
                                <SkuImage
                                  imagePath={inventoryItems.find(inv => inv.sku_code === item.sku)?.image_path}
                                  alt="SKU"
                                  width={32}
                                  height={32}
                                  enableModal={true}
                                  showBlankWhenNoImage={true}
                                  key={`${item.sku}-${lastInventoryRefresh}`}
                                />
                                <SKUSearchInput
                                  type="text"
                                  value={skuSearchTerms[item.id] !== undefined ? skuSearchTerms[item.id] : (item.sku || '')}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setSKUSearchTerms(prev => ({
                                      ...prev,
                                      [item.id]: newValue
                                    }));
                                    setOpenSKUDropdowns(prev => ({
                                      ...prev,
                                      [item.id]: true
                                    }));
                                    // If the search term is cleared, also clear the item SKU
                                    if (!newValue.trim()) {
                                      handleItemChange(item.id, 'sku', '');
                                    }
                                  }}
                                  onClick={() => {
                                    // Open dropdown when input is clicked
                                    setOpenSKUDropdowns(prev => ({
                                      ...prev,
                                      [item.id]: true
                                    }));
                                  }}
                                  onFocus={() => {
                                    // Set search term to current SKU when focused
                                    setSKUSearchTerms(prev => ({
                                      ...prev,
                                      [item.id]: item.sku || ''
                                    }));
                                    setOpenSKUDropdowns(prev => ({
                                      ...prev,
                                      [item.id]: true
                                    }));
                                  }}
                                  placeholder="Search SKU"
                                  style={{ flex: 1 }}
                                />
                              </SelectedSKUContainer>
                            ) : (
                              <SKUSearchInput
                                type="text"
                                value={skuSearchTerms[item.id] !== undefined ? skuSearchTerms[item.id] : (item.sku || '')}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setSKUSearchTerms(prev => ({
                                    ...prev,
                                    [item.id]: newValue
                                  }));
                                  setOpenSKUDropdowns(prev => ({
                                    ...prev,
                                    [item.id]: true
                                  }));
                                  // Update the item SKU as user types
                                  handleItemChange(item.id, 'sku', newValue);
                                }}
                                onClick={() => {
                                  // Open dropdown when input is clicked
                                  setOpenSKUDropdowns(prev => ({
                                    ...prev,
                                    [item.id]: true
                                  }));
                                }}
                                onFocus={() => setOpenSKUDropdowns(prev => ({
                                  ...prev,
                                  [item.id]: true
                                }))}
                                placeholder="Search SKU"
                              />
                            )}
                            <SKUDropdown isOpen={openSKUDropdowns[item.id] && inventoryItems.length > 0}>
                              {isLoadingInventory ? (
                                <SKUOption>
                                  <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                    Loading SKUs...
                                  </div>
                                </SKUOption>
                              ) : !supplier || !supplierId ? (
                                <SKUOption>
                                  <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                    Please select a supplier first
                                  </div>
                                </SKUOption>
                              ) : filteredInventoryItemsByRow[item.id]?.length > 0 ? (
                                filteredInventoryItemsByRow[item.id].map(invItem => (
                                  <SKUOption
                                    key={invItem.id}
                                    onClick={() => handleSKUSelect(item.id, invItem)}
                                  >
                                    <SkuImage
                                      imagePath={invItem.image_path}
                                      alt="SKU"
                                      width={40}
                                      height={40}
                                      enableModal={true}
                                      showBlankWhenNoImage={true}
                                      key={`${invItem.id}-${lastInventoryRefresh}`}
                                    />
                                    <SKUOptionContent>
                                      <div style={{ fontWeight: '500' }}>
                                        <SafeText>{invItem.sku_code}</SafeText>
                                      </div>
                                      {invItem.description && (
                                        <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '2px' }}>
                                          <SafeText>{invItem.description}</SafeText>
                                        </div>
                                      )}
                                    </SKUOptionContent>
                                  </SKUOption>
                                ))
                              ) : skuSearchTerms[item.id]?.trim() ? (
                                <SKUOption>
                                  <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                    No matching SKUs found for this supplier
                                  </div>
                                </SKUOption>
                              ) : (
                                <SKUOption>
                                  <div style={{ padding: '8px', textAlign: 'center', color: '#6B7280' }}>
                                    Type to search for SKUs
                                  </div>
                                </SKUOption>
                              )}
                            </SKUDropdown>
                          </SKUSelectWrapper>
                        </td>
                        <td className="wrap">
                          <FormInput
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <FormInput
                            type="number"
                            className="numeric-input"
                            value={item.quantityOrdered || ''}
                            onChange={(e) => handleItemChange(item.id, 'quantityOrdered', e.target.value)}
                            min="0"
                            required
                          />
                        </td>
                        <td>
                          <DateInputWrapper>
                            <FormInput
                              type="date"
                              value={item.expectedDeliveryDate}
                              onChange={(e) => handleItemChange(item.id, 'expectedDeliveryDate', e.target.value)}
                            />
                            <svg 
                              className="calendar-icon" 
                              xmlns="http://www.w3.org/2000/svg" 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="#6B7280" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                          </DateInputWrapper>
                        </td>
                        <td>
                          <CurrencyInputWrapper>
                            <span>$</span>
                            <input
                              type="text"
                              value={item.total ? item.total.toFixed(2) : '0.00'}
                              readOnly
                              style={{
                                backgroundColor: '#F9FAFB',
                                cursor: 'not-allowed',
                                color: '#111827',
                                fontWeight: '500'
                              }}
                            />
                          </CurrencyInputWrapper>
                        </td>
                        <td>
                          <ActionButtonsContainer>
                            {items.findIndex(i => i.id === item.id) === items.length - 1 && (
                              <AddButton onClick={handleAddItem} type="button">
                                +
                              </AddButton>
                            )}
                            {items.length > 1 && (
                              <RemoveButton
                                type="button"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                ×
                              </RemoveButton>
                            )}
                          </ActionButtonsContainer>
                        </td>
                      </tr>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <SummaryContainer>
                <PaymentTermsContainer>
                  <FormLabel>Payment Terms:</FormLabel>
                  <FormInput
                    type="text"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                  />
                </PaymentTermsContainer>

                <CostItem>
                  <span>Total amount:</span>
                  <div className="amount">
                    <CurrencyInputWrapper>
                      <span>$</span>
                      <input
                        type="number"
                        value={totalAmount || ''}
                        onChange={(e) => setTotalAmount(Number(e.target.value))}
                        step="0.01"
                        min="0"
                      />
                    </CurrencyInputWrapper>
                  </div>
                </CostItem>
                <CostItem>
                  <span>GST (10% of total amount):</span>
                  <div className="amount">
                    <CurrencyInputWrapper>
                      <span>$</span>
                      <input
                        type="number"
                        value={gstAmount || ''}
                        onChange={(e) => {
                          setGstAmount(Number(e.target.value));
                          setTotalPayable(totalAmount + Number(e.target.value));
                        }}
                        step="0.01"
                        min="0"
                      />
                    </CurrencyInputWrapper>
                  </div>
                </CostItem>
                <CostItem>
                  <span>Total Payable:</span>
                  <div className="amount">
                    <CurrencyInputWrapper>
                      <span>$</span>
                      <input
                        type="number"
                        value={totalPayable || ''}
                        onChange={(e) => setTotalPayable(Number(e.target.value))}
                        step="0.01"
                        min="0"
                      />
                    </CurrencyInputWrapper>
                  </div>
                </CostItem>
              </SummaryContainer>

              <ButtonContainer>
                <CancelButton type="button" onClick={handleCancel}>
                  Cancel
                </CancelButton>
                <DraftButton type="button" onClick={handleSaveAsDraft} disabled={loading}>
                  {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ButtonSkeleton style={{ width: '80%', maxWidth: '120px' }} />
                    </div>
                  ) : isEditMode ? 'Update Draft' : 'Save as Draft'}
                </DraftButton>
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ButtonSkeleton style={{ width: '80%', maxWidth: '120px' }} />
                    </div>
                  ) : isEditMode ? 'Update & Submit' : 'Submit'}
                </SubmitButton>
              </ButtonContainer>
            </form>
          </div>
        )}

        {showNotesModal && (
          <ModalOverlay onClick={() => {
            setShowNotesModal(false);
            setCurrentNotes('');
            setCurrentItemId('');
          }}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Product Notes</ModalTitle>
              <NotesTextarea
                value={currentNotes}
                readOnly
                placeholder="No notes available for this product"
                style={{ backgroundColor: '#F9FAFB', cursor: 'default' }}
              />
              <ModalButtonContainer>
                <CloseButton onClick={() => {
                  setShowNotesModal(false);
                  setCurrentNotes('');
                  setCurrentItemId('');
                }}>Close</CloseButton>
              </ModalButtonContainer>
            </ModalContent>
          </ModalOverlay>
        )}

        {toast.isVisible && (
          <ToastComponent
            message={toast.message}
            type={toast.type}
            onClose={() => { }}
          />
        )}
      </PageContainer>

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        isOpen={showExitConfirmation}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />
    </Layout>
    </>
  );
};

export default CreatePOPage;